import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Crown, Send, FileText, Sparkles, Download, Loader2, Mail, Mic, MicOff,
  ScrollText, Megaphone, ClipboardList, Briefcase, MessageSquare, Lock,
  Zap, Users, Search, CheckCircle2, RefreshCw, Bot, ArrowUpRight, Command,
  X, PlayCircle, ShieldAlert, BarChart3, Radio, Coffee, HeartHandshake,
  BellRing, Wand2, PenTool, Clock, ChevronRight, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField } from '../../components/ui/jewel-modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_DRAFTS = [
  { id: 'memo',         label: 'Internal Memo',    icon: ScrollText,    hex: '#F43F5E', type: 'memo' },
  { id: 'email',        label: 'Executive Email',  icon: Mail,          hex: '#F97316', type: 'email' },
  { id: 'announcement', label: 'Announcement',     icon: Megaphone,     hex: '#F59E0B', type: 'announcement' },
  { id: 'policy',       label: 'Policy Update',    icon: FileText,      hex: '#8A2BE2', type: 'policy' },
  { id: 'agenda',       label: 'Meeting Agenda',   icon: ClipboardList, hex: '#00E5FF', type: 'agenda' },
  { id: 'press',        label: 'Press Release',    icon: Briefcase,     hex: '#10B981', type: 'press_release' },
];

/* ══════════════════════════════════════════════════════════════════
   PRIMITIVES
   ══════════════════════════════════════════════════════════════════ */
const BentoCard = ({ children, className = '', accent = '#F43F5E' }) => (
  <div className={`relative overflow-hidden rounded-3xl border border-white/[0.06] backdrop-blur-md ${className}`}
    style={{ background: 'linear-gradient(140deg, rgba(10,10,20,0.65) 0%, rgba(8,8,18,0.85) 100%)', boxShadow: '0 20px 50px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.03)' }}>
    <div className="pointer-events-none absolute -top-24 -right-16 w-56 h-56 rounded-full blur-[100px] opacity-40" style={{ background: `radial-gradient(circle, ${accent}30 0%, transparent 70%)` }} />
    <div className="relative">{children}</div>
  </div>
);

const KpiTile = ({ icon: Icon, label, value, sub, accent, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="group relative overflow-hidden rounded-2xl border border-white/[0.07] bg-[#0A0A12]/70 p-4 text-left transition-all hover:border-white/[0.16] hover:-translate-y-0.5 w-full"
    style={{ boxShadow: '0 12px 30px -15px rgba(0,0,0,0.5)' }}>
    <div className="pointer-events-none absolute -top-8 -right-6 w-28 h-28 rounded-full blur-[70px] opacity-50 group-hover:opacity-90 transition-opacity" style={{ background: accent }} />
    <div className="relative">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}18`, border: `1px solid ${accent}40` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={1.8} />
        </div>
        <span className="text-[9px] tracking-[0.2em] uppercase text-white/40 font-medium">{label}</span>
      </div>
      <p className="text-white text-2xl font-light tabular-nums leading-none">{value}</p>
      {sub && <p className="text-white/40 text-[10px] mt-1.5">{sub}</p>}
    </div>
  </button>
);

const LuxuryPillBtn = ({ onClick, icon: Icon, children, hex = '#F43F5E', className = '', disabled, testid }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className={`group relative inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-medium tracking-[0.15em] uppercase overflow-hidden transition-all disabled:opacity-40 hover:-translate-y-0.5 ${className}`}
    style={{ background: `linear-gradient(135deg, ${hex}, #F97316)`, color: '#0a0a14', boxShadow: `0 12px 28px -10px ${hex}66` }}>
    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
    {Icon && <Icon className="w-3.5 h-3.5 relative" />}
    <span className="relative">{children}</span>
  </button>
);

/* ══════════════════════════════════════════════════════════════════
   MAIN — CEO Cockpit
   ══════════════════════════════════════════════════════════════════ */
const NovaPAPage = ({ getAuthHeaders, currentUser }) => {
  const isCEO = currentUser?.role === 'superadmin';

  // Chat
  const [messages, setMessages] = useState([{ role: 'assistant', content: `Welcome, **${currentUser?.display_name || 'Bella'}**.\n\nI'm your AI Chief of Staff — reading the whole company in real time. Ask me anything, draft anything, and I'll handle the rest.`, timestamp: new Date() }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  // Voice
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  // Briefing
  const [briefing, setBriefing] = useState(null);
  const [askInput, setAskInput] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [askResult, setAskResult] = useState(null);
  const [actionRunning, setActionRunning] = useState(null);

  // Command Palette
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Draft modal
  const [showDraft, setShowDraft] = useState(false);
  const [draftType, setDraftType] = useState('memo');
  const [draftForm, setDraftForm] = useState({ title: '', instructions: '', audience: '', tone: 'professional', bullet_points: '' });
  const [drafting, setDrafting] = useState(false);
  const [draftResult, setDraftResult] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Meeting Brief
  const [showBrief, setShowBrief] = useState(false);
  const [briefUsername, setBriefUsername] = useState('');
  const [briefPurpose, setBriefPurpose] = useState('1:1 catch-up');
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefResult, setBriefResult] = useState(null);

  // Announcement
  const [announceModal, setAnnounceModal] = useState(false);
  const [announceForm, setAnnounceForm] = useState({ title: 'Announcement from the CEO', message: '' });

  const messagesEndRef = useRef(null);

  const fetchBriefing = useCallback(async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/nova-pa/briefing`, { headers: getAuthHeaders() }); setBriefing(r.data); } catch {}
  }, [getAuthHeaders]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (isCEO) { fetchBriefing(); const t = setInterval(fetchBriefing, 60000); return () => clearInterval(t); } }, [fetchBriefing, isCEO]);

  // Global ⌘K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen(o => !o); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Voice input via browser SpeechRecognition
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error('Voice input needs Chrome or Edge');
    if (isListening) { recognitionRef.current?.stop(); return; }
    const rec = new SR();
    rec.continuous = false; rec.interimResults = true; rec.lang = 'en-GB';
    rec.onstart = () => setIsListening(true);
    rec.onend = () => setIsListening(false);
    rec.onerror = () => { setIsListening(false); toast.error('Voice input failed'); };
    rec.onresult = (event) => {
      const transcript = Array.from(event.results).map(r => r[0].transcript).join('');
      setInput(transcript);
      if (event.results[event.results.length - 1].isFinal) setTimeout(() => { sendMessage(transcript); }, 200);
    };
    recognitionRef.current = rec; rec.start();
  };

  const sendMessage = async (text) => {
    if (!text?.trim() || sending) return;
    const userMsg = { role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]); setInput(''); setSending(true);
    try {
      const history = messages.slice(-12).map(m => ({ sender: m.role === 'user' ? 'user' : 'ai', message: m.content }));
      const r = await axios.post(`${BACKEND_URL}/api/nova-pa/chat`, { message: text, conversation_history: history }, { headers: getAuthHeaders() });
      setMessages(prev => [...prev, { role: 'assistant', content: r.data.response, timestamp: new Date() }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nova PA unavailable');
      setMessages(prev => [...prev, { role: 'assistant', content: '_Briefly unavailable — please try again._', timestamp: new Date(), error: true }]);
    }
    setSending(false);
  };

  const runAsk = async () => {
    if (!askInput.trim() || askLoading) return;
    setAskLoading(true); setAskResult(null);
    try { const r = await axios.post(`${BACKEND_URL}/api/nova-pa/ask`, { query: askInput }, { headers: getAuthHeaders() }); setAskResult(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Ask failed'); }
    setAskLoading(false);
  };

  const runQuickAction = async (action, promptText) => {
    if (!window.confirm(promptText)) return;
    setActionRunning(action);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/nova-pa/quick-action`, { action }, { headers: getAuthHeaders() });
      const msg = action === 'approve_all_pending_leave' ? `Approved ${r.data.approved_count || 0} leave`
        : action === 'force_resolve_stale_chats' ? `Resolved ${r.data.resolved_count || 0} stale chats`
        : action === 'clear_old_audit_logs' ? `Deleted ${r.data.deleted_count || 0} old entries`
        : 'Done';
      toast.success(msg); fetchBriefing();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    setActionRunning(null);
  };

  const generateDraft = async () => {
    if (!draftForm.title || !draftForm.instructions) return toast.error('Title and instructions required');
    setDrafting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/nova-pa/draft-document`, {
        document_type: draftType, title: draftForm.title, instructions: draftForm.instructions,
        audience: draftForm.audience, tone: draftForm.tone,
        bullet_points: draftForm.bullet_points.split('\n').map(s => s.trim()).filter(Boolean),
      }, { headers: getAuthHeaders() });
      setDraftResult(r.data); toast.success('Draft ready');
    } catch (e) { toast.error(e.response?.data?.detail || 'Drafting failed'); }
    setDrafting(false);
  };

  const downloadPDF = async (title, body, subtitle = '') => {
    setGeneratingPdf(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/nova-pa/generate-pdf`, {
        title, subtitle, body_markdown: body, document_type: draftType,
      }, { headers: getAuthHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${title.replace(/\s+/g, '_')}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('PDF export failed'); }
    setGeneratingPdf(false);
  };

  const generateMeetingBrief = async () => {
    if (!briefUsername.trim()) return toast.error('Which staff member?');
    setBriefLoading(true); setBriefResult(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/nova-pa/meeting-brief`, {
        staff_username: briefUsername.trim(), meeting_purpose: briefPurpose,
      }, { headers: getAuthHeaders() });
      setBriefResult(r.data); toast.success('Brief ready');
    } catch (e) { toast.error(e.response?.data?.detail || 'Brief failed'); }
    setBriefLoading(false);
  };

  const broadcastAnnouncement = async () => {
    if (!announceForm.message.trim()) return toast.error('Write the announcement first');
    setActionRunning('broadcast_announcement');
    try {
      await axios.post(`${BACKEND_URL}/api/nova-pa/quick-action`, { action: 'broadcast_announcement', payload: announceForm }, { headers: getAuthHeaders() });
      toast.success('Broadcast sent to all staff'); setAnnounceModal(false);
      setAnnounceForm({ title: 'Announcement from the CEO', message: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Broadcast failed'); }
    setActionRunning(null);
  };

  if (!isCEO) {
    return (
      <div className="flex items-center justify-center h-[70vh]" data-testid="nova-pa-denied">
        <div className="text-center">
          <Lock className="w-14 h-14 text-rose-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-1">Nova PA — CEO Only</h2>
          <p className="text-white/50 text-sm">This personal assistant is exclusive to the Chief Executive.</p>
        </div>
      </div>
    );
  }

  const kpi = briefing?.kpis || {};

  return (
    <div className="pb-12 min-h-screen relative" data-testid="nova-pa-page">
      <div className="fixed top-0 left-1/4 w-[700px] h-[500px] bg-rose-500/[0.04] rounded-full blur-[180px] pointer-events-none -z-10" />
      <div className="fixed bottom-0 right-1/4 w-[600px] h-[400px] bg-orange-500/[0.04] rounded-full blur-[160px] pointer-events-none -z-10" />

      {/* ═══ HERO ═══ */}
      <div className="relative overflow-hidden rounded-[32px] border border-white/[0.06] p-8 mb-5"
        style={{ background: 'linear-gradient(140deg, rgba(20,8,15,0.75) 0%, rgba(8,8,18,0.9) 100%)', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)' }}>
        <div className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full blur-[130px] bg-rose-500/25" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 rounded-full blur-[130px] bg-orange-500/20" />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, rgba(244,63,94,0.30), rgba(249,115,22,0.25))', border: '1px solid rgba(244,63,94,0.45)', boxShadow: '0 20px 50px -12px rgba(244,63,94,0.55)' }}>
              <Crown className="w-7 h-7 text-rose-200" strokeWidth={1.4} />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-rose-300/70 font-medium mb-1">Executive · Nova PA</p>
              <h1 className="text-white text-4xl md:text-5xl font-light tracking-tight">Nova <span className="bg-gradient-to-r from-rose-300 to-orange-300 bg-clip-text text-transparent">Cockpit</span></h1>
              <p className="text-white/45 text-sm mt-1">Your AI Chief of Staff — reading the whole company in real time</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setPaletteOpen(true)} data-testid="cmdk-btn"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] tracking-[0.14em] uppercase font-medium text-white/70 bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.06] hover:text-white transition-all">
              <Command className="w-3 h-3" /> Command palette <span className="ml-1 px-1.5 py-0.5 rounded bg-white/[0.06] text-white/50 text-[9px]">⌘K</span>
            </button>
            <LuxuryPillBtn icon={RefreshCw} onClick={fetchBriefing} testid="refresh-briefing-btn">Refresh</LuxuryPillBtn>
          </div>
        </div>
      </div>

      {/* ═══ LIVE KPI TILES ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
        <KpiTile icon={Radio}   label="In queue"     value={kpi.live_chat?.waiting ?? '—'}       accent="#00E5FF" sub={`${kpi.live_chat?.in_progress ?? 0} in progress`} testid="kpi-queue" />
        <KpiTile icon={Coffee}  label="Online staff" value={kpi.staff?.online_now ?? '—'}        accent="#10B981" sub={`${kpi.staff?.total ?? 0} total`} testid="kpi-online" />
        <KpiTile icon={Clock}   label="Leave pending" value={kpi.leave?.pending ?? '—'}          accent="#F59E0B" sub={`${kpi.leave?.approved_this_week ?? 0} approved this week`} testid="kpi-leave" />
        <KpiTile icon={BarChart3} label="CSAT (30d)" value={kpi.live_chat?.csat_avg ? `${kpi.live_chat.csat_avg}/5` : '—'} accent="#F472B6" sub={`${kpi.live_chat?.csat_count ?? 0} ratings`} testid="kpi-csat" />
        <KpiTile icon={ShieldAlert} label="Security 24h" value={kpi.security?.events_24h ?? '—'} accent="#F43F5E" sub={`${kpi.security?.active_blocks ?? 0} active blocks`} testid="kpi-security" />
        <KpiTile icon={HeartHandshake} label="Feedback 7d" value={kpi.engagement?.feedback_this_week ?? '—'} accent="#8A2BE2" testid="kpi-feedback" />
      </div>

      {/* ═══ PRIORITY INBOX + ASK ═══ */}
      <div className="grid lg:grid-cols-5 gap-4 mb-5">
        <BentoCard accent="#F43F5E" className="lg:col-span-3">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <BellRing className="w-4 h-4 text-rose-300" />
              <span className="text-[10px] tracking-[0.22em] uppercase text-white/50 font-medium">Priority inbox</span>
              {briefing?.alerts?.length ? (
                <span className="ml-auto px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.14em] uppercase border border-rose-400/30 bg-rose-500/10 text-rose-200">{briefing.alerts.length}</span>
              ) : null}
            </div>
            {(!briefing?.alerts || briefing.alerts.length === 0) ? (
              <div className="text-center py-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-emerald-300 text-sm">Nothing needs your attention — enjoy the calm</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="priority-inbox-list">
                {briefing.alerts.map((a, i) => {
                  const hex = a.level === 'critical' ? '#F43F5E' : a.level === 'high' ? '#F59E0B' : a.level === 'medium' ? '#8A2BE2' : '#94A3B8';
                  return (
                    <a key={i} href={a.link || '#'} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12] transition-all group">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: hex, boxShadow: `0 0 10px ${hex}` }} />
                      <span className="flex-1 text-white text-sm">{a.title}</span>
                      <span className="text-[9px] tracking-[0.14em] uppercase font-semibold" style={{ color: hex }}>{a.level}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-white/30 group-hover:text-white/70" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </BentoCard>

        <BentoCard accent="#00E5FF" className="lg:col-span-2">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Wand2 className="w-4 h-4 text-cyan-300" />
              <span className="text-[10px] tracking-[0.22em] uppercase text-white/50 font-medium">Ask Nova</span>
            </div>
            <p className="text-white/50 text-[11px] mb-3">Live operational queries — Nova reads the whole system.</p>
            <div className="flex gap-2 mb-3">
              <input type="text" value={askInput} onChange={(e) => setAskInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && runAsk()} placeholder="Who's waiting on chat?"
                data-testid="nova-ask-input"
                className="flex-1 bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2 text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-400/40" />
              <button onClick={runAsk} disabled={askLoading || !askInput.trim()} data-testid="nova-ask-btn"
                className="px-4 rounded-xl text-[11px] font-semibold tracking-[0.14em] uppercase bg-gradient-to-r from-cyan-500 to-violet-500 text-black disabled:opacity-40">
                {askLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Ask'}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {["Who's waiting on chat?", 'Show pending leave', 'CSAT this month', 'Staff online now'].map((s, i) => (
                <button key={i} onClick={() => { setAskInput(s); setTimeout(runAsk, 100); }}
                  className="text-[10px] tracking-[0.05em] uppercase px-2 py-1 rounded-full bg-white/[0.03] hover:bg-cyan-500/10 text-white/50 hover:text-cyan-200 border border-white/[0.06]">
                  {s}
                </button>
              ))}
            </div>
            {askResult && (
              <div className="mt-3 p-3 rounded-xl bg-cyan-500/[0.05] border border-cyan-400/20" data-testid="nova-ask-result">
                <p className="text-[9px] tracking-[0.18em] uppercase text-cyan-300/70 mb-2">Intent · {askResult.intent}</p>
                <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-strong:text-cyan-200">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{askResult.answer}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        </BentoCard>
      </div>

      {/* ═══ QUICK ACTIONS + DRAFT GRID ═══ */}
      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        <BentoCard accent="#F59E0B" className="lg:col-span-2">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-amber-300" />
              <span className="text-[10px] tracking-[0.22em] uppercase text-white/50 font-medium">Executive quick actions</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                { id: 'approve_all_pending_leave',  hex: '#10B981', icon: CheckCircle2, label: 'Approve ALL pending leave',   prompt: 'Approve every pending leave request in one go?' },
                { id: 'broadcast_announcement',     hex: '#F59E0B', icon: Megaphone,    label: 'Broadcast announcement',      prompt: null },
                { id: 'force_resolve_stale_chats',  hex: '#00E5FF', icon: Radio,        label: 'Resolve stale (>24h) chats',  prompt: 'Force-resolve every live chat idle for 24h+?' },
                { id: 'meeting_brief',              hex: '#8A2BE2', icon: Users,        label: 'Generate meeting brief',      prompt: null },
              ].map(a => (
                <button key={a.id} onClick={() => {
                  if (a.id === 'broadcast_announcement') return setAnnounceModal(true);
                  if (a.id === 'meeting_brief') return setShowBrief(true);
                  runQuickAction(a.id, a.prompt);
                }}
                  data-testid={`quick-action-${a.id}`}
                  disabled={actionRunning === a.id}
                  className="group relative overflow-hidden p-3 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:border-white/[0.15] text-left transition-all flex items-center gap-3">
                  <div className="pointer-events-none absolute -top-4 -right-4 w-20 h-20 rounded-full blur-[50px] opacity-40 group-hover:opacity-80 transition-opacity" style={{ background: a.hex }} />
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${a.hex}18`, border: `1px solid ${a.hex}35` }}>
                    {actionRunning === a.id ? <Loader2 className="w-4 h-4 animate-spin text-white/80" /> : <a.icon className="w-4 h-4" style={{ color: a.hex }} />}
                  </div>
                  <span className="text-white text-xs font-medium relative">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        </BentoCard>

        <BentoCard accent="#8A2BE2">
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <PenTool className="w-4 h-4 text-violet-300" />
              <span className="text-[10px] tracking-[0.22em] uppercase text-white/50 font-medium">Draft in one click</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_DRAFTS.map(d => (
                <button key={d.id} onClick={() => { setDraftType(d.type); setDraftForm({ title: '', instructions: '', audience: '', tone: 'professional', bullet_points: '' }); setDraftResult(null); setShowDraft(true); }}
                  data-testid={`draft-${d.id}`}
                  className="group relative overflow-hidden p-3 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:border-white/[0.15] transition-all flex flex-col items-center gap-1.5">
                  <div className="pointer-events-none absolute -top-4 -right-4 w-16 h-16 rounded-full blur-[40px] opacity-40 group-hover:opacity-80 transition-opacity" style={{ background: d.hex }} />
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center relative" style={{ background: `${d.hex}18`, border: `1px solid ${d.hex}35` }}>
                    <d.icon className="w-4 h-4" style={{ color: d.hex }} />
                  </div>
                  <span className="text-white/80 text-[10px] font-medium tracking-wider relative text-center leading-tight">{d.label}</span>
                </button>
              ))}
            </div>
          </div>
        </BentoCard>
      </div>

      {/* ═══ CHAT PANEL ═══ */}
      <BentoCard accent="#F43F5E">
        <div className="flex flex-col h-[520px]">
          <div className="px-5 py-3.5 border-b border-white/[0.05] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-rose-300" />
              <span className="text-white text-sm font-medium">Private conversation</span>
            </div>
            <span className="text-[9px] uppercase tracking-[0.2em] text-emerald-400/70 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Nova online
            </span>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`pa-msg-${i}`}>
                {m.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center flex-shrink-0 mr-2 mt-1 shadow-lg shadow-rose-500/25">
                    <Crown className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                  m.role === 'user' ? 'bg-gradient-to-br from-rose-500 to-orange-500 text-white shadow-lg shadow-rose-500/20'
                    : m.error ? 'bg-rose-500/10 text-rose-200 border border-rose-500/25'
                    : 'bg-white/[0.04] text-white/85 border border-white/[0.06]'
                }`}>
                  {m.role === 'assistant' ? (
                    <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-headings:text-rose-200 prose-strong:text-white prose-code:text-orange-300 prose-code:bg-black/40 prose-code:px-1 prose-code:rounded">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    </div>
                  ) : <span>{m.content}</span>}
                  {m.role === 'assistant' && /```|^#/.test(m.content) && !m.error && (
                    <button onClick={() => downloadPDF('Nova PA Draft', m.content)} disabled={generatingPdf} data-testid={`pa-msg-pdf-${i}`}
                      className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-rose-500/15 hover:bg-rose-500/25 text-rose-200 text-[10px] border border-rose-500/25">
                      {generatingPdf ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PDF
                    </button>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center flex-shrink-0 mr-2">
                  <Crown className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white/[0.04] border border-white/[0.06] rounded-2xl px-4 py-2.5 flex items-center gap-2">
                  <span className="text-rose-300 text-xs">Nova is composing</span>
                  <span className="flex gap-0.5">{[0, 200, 400].map(d => (<span key={d} className="w-1 h-1 bg-rose-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />))}</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-4 border-t border-white/[0.05]">
            <div className="flex gap-2 items-end">
              <button onClick={toggleVoice} data-testid="voice-btn"
                className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${isListening ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/50 animate-pulse' : 'bg-white/[0.04] border border-white/[0.08] text-white/50 hover:text-white'}`}>
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <textarea value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                placeholder={isListening ? 'Listening…' : 'Ask, draft, decide — voice or type'} rows={1} disabled={sending}
                data-testid="pa-input"
                className="flex-1 resize-none bg-black/40 border border-white/[0.08] focus:border-rose-500/40 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 outline-none max-h-32" />
              <button onClick={() => sendMessage(input)} disabled={sending || !input.trim()} data-testid="pa-send-btn"
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-lg shadow-rose-500/25 disabled:opacity-40">
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-white/25 text-[10px] mt-2 text-center tracking-[0.2em] uppercase">
              Enter to send · Shift+Enter for new line · <kbd className="px-1 py-0.5 rounded bg-white/[0.06] font-mono">⌘K</kbd> for palette
            </p>
          </div>
        </div>
      </BentoCard>

      {/* ═══ MODALS ═══ */}
      {/* Command palette */}
      <JewelModal open={paletteOpen} onOpenChange={setPaletteOpen} icon={Command} title="Command palette" subtitle="Search staff · run actions · jump anywhere" accent="cyan" size="md" testid="cmdk-modal">
        <div className="space-y-3">
          <input type="text" value={askInput} onChange={(e) => setAskInput(e.target.value)}
            onKeyPress={(e) => { if (e.key === 'Enter') { runAsk(); setPaletteOpen(false); } }}
            placeholder="Ask Nova · search · type a command…" autoFocus
            data-testid="cmdk-input"
            className="w-full px-4 py-3 bg-black/40 border border-white/[0.10] rounded-xl text-white focus:border-cyan-400/40 focus:outline-none" />
          <div className="text-[10px] tracking-[0.18em] uppercase text-white/40 mb-1">Jump to</div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Meeting brief', icon: Users, hex: '#8A2BE2', onClick: () => { setPaletteOpen(false); setShowBrief(true); } },
              { label: 'Broadcast',      icon: Megaphone, hex: '#F59E0B', onClick: () => { setPaletteOpen(false); setAnnounceModal(true); } },
              { label: 'Draft memo',     icon: ScrollText, hex: '#F43F5E', onClick: () => { setPaletteOpen(false); setDraftType('memo'); setShowDraft(true); } },
              { label: 'Refresh KPIs',   icon: RefreshCw, hex: '#10B981', onClick: () => { setPaletteOpen(false); fetchBriefing(); } },
            ].map((c, i) => (
              <button key={i} onClick={c.onClick} data-testid={`cmdk-action-${i}`}
                className="flex items-center gap-2 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.15] transition-all text-left">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${c.hex}18`, border: `1px solid ${c.hex}35` }}>
                  <c.icon className="w-3.5 h-3.5" style={{ color: c.hex }} />
                </div>
                <span className="text-white/80 text-xs">{c.label}</span>
              </button>
            ))}
          </div>
        </div>
      </JewelModal>

      {/* Meeting brief modal */}
      <JewelModal open={showBrief} onOpenChange={setShowBrief} icon={Users} title="Meeting brief" subtitle="AI pre-meeting brief on any staff member" accent="violet" size="lg" testid="brief-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowBrief(false)}>Close</JewelButton>
          <JewelButton accent="violet" icon={Sparkles} onClick={generateMeetingBrief} disabled={briefLoading} loading={briefLoading} data-testid="brief-generate-btn">Generate brief</JewelButton>
        </>}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <JewelField label="Staff username" required>
              <JewelInput value={briefUsername} onChange={(e) => setBriefUsername(e.target.value)} placeholder="e.g. owen" data-testid="brief-username-input" />
            </JewelField>
            <JewelField label="Meeting purpose">
              <JewelInput value={briefPurpose} onChange={(e) => setBriefPurpose(e.target.value)} placeholder="1:1, review, disciplinary…" />
            </JewelField>
          </div>
          {briefResult && (
            <div className="relative overflow-hidden rounded-2xl border border-violet-400/25 bg-gradient-to-br from-violet-500/[0.06] to-fuchsia-500/[0.03] p-4" data-testid="brief-result">
              <div className="mb-3">
                <p className="text-[9px] tracking-[0.2em] uppercase text-violet-300/70 mb-0.5">Brief for {briefResult.staff.display_name}</p>
                <p className="text-white text-sm">{briefResult.staff.job_title || briefResult.staff.role} · {briefResult.staff.department || 'No department'}</p>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {[
                  ['HR notices', briefResult.signals.hr_notices_recent],
                  ['Active warnings', briefResult.signals.active_warnings],
                  ['Leave 30d', briefResult.signals.approved_leave_30d],
                  ['Leave pending', briefResult.signals.pending_leave],
                ].map(([l, v]) => (
                  <div key={l} className="p-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-center">
                    <p className="text-white text-lg font-light tabular-nums leading-none">{v}</p>
                    <p className="text-[9px] tracking-wider uppercase text-white/40 mt-1">{l}</p>
                  </div>
                ))}
              </div>
              <div className="prose prose-invert prose-sm max-w-none prose-strong:text-violet-200 prose-li:my-1">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{briefResult.talking_points}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </JewelModal>

      {/* Draft modal */}
      <JewelModal open={showDraft} onOpenChange={setShowDraft} icon={PenTool} title={`Draft ${QUICK_DRAFTS.find(d => d.type === draftType)?.label || 'document'}`} subtitle="Nova drafts, you approve" accent="rose" size="lg" testid="draft-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowDraft(false)}>Close</JewelButton>
          {draftResult && <JewelButton variant="ghost" icon={Download} onClick={() => downloadPDF(draftForm.title, draftResult.body)} disabled={generatingPdf} data-testid="draft-pdf-btn">PDF</JewelButton>}
          <JewelButton accent="rose" icon={Sparkles} onClick={generateDraft} disabled={drafting} loading={drafting} data-testid="draft-generate-btn">Generate</JewelButton>
        </>}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <JewelField label="Title" required><JewelInput value={draftForm.title} onChange={(e) => setDraftForm({ ...draftForm, title: e.target.value })} data-testid="draft-title-input" /></JewelField>
            <JewelField label="Tone">
              <JewelSelect value={draftForm.tone} onChange={(e) => setDraftForm({ ...draftForm, tone: e.target.value })}>
                <option value="professional">Professional</option>
                <option value="warm">Warm</option>
                <option value="firm">Firm</option>
                <option value="celebratory">Celebratory</option>
              </JewelSelect>
            </JewelField>
          </div>
          <JewelField label="Audience"><JewelInput value={draftForm.audience} onChange={(e) => setDraftForm({ ...draftForm, audience: e.target.value })} placeholder="e.g. leadership team, all staff, external partner" /></JewelField>
          <JewelField label="Instructions" required><JewelTextarea rows={3} value={draftForm.instructions} onChange={(e) => setDraftForm({ ...draftForm, instructions: e.target.value })} placeholder="What should this say? Nova will handle the rest." /></JewelField>
          <JewelField label="Bullet points (one per line)"><JewelTextarea rows={3} value={draftForm.bullet_points} onChange={(e) => setDraftForm({ ...draftForm, bullet_points: e.target.value })} placeholder={"Key point 1\nKey point 2"} /></JewelField>
          {draftResult && (
            <div className="relative overflow-hidden rounded-2xl border border-rose-400/25 bg-rose-500/[0.03] p-4 max-h-[40vh] overflow-y-auto" data-testid="draft-result">
              <div className="prose prose-invert prose-sm max-w-none prose-strong:text-rose-200">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{draftResult.body}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </JewelModal>

      {/* Announcement modal */}
      <JewelModal open={announceModal} onOpenChange={setAnnounceModal} icon={Megaphone} title="Broadcast to all staff" subtitle="Every account will get an in-app notification" accent="amber" size="md" testid="announce-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setAnnounceModal(false)}>Cancel</JewelButton>
          <JewelButton accent="amber" icon={Megaphone} onClick={broadcastAnnouncement} disabled={actionRunning === 'broadcast_announcement'} data-testid="announce-send-btn">Broadcast</JewelButton>
        </>}>
        <div className="space-y-3">
          <JewelField label="Title"><JewelInput value={announceForm.title} onChange={(e) => setAnnounceForm({ ...announceForm, title: e.target.value })} /></JewelField>
          <JewelField label="Message" required><JewelTextarea rows={4} value={announceForm.message} onChange={(e) => setAnnounceForm({ ...announceForm, message: e.target.value })} placeholder="Team, I wanted to share…" /></JewelField>
        </div>
      </JewelModal>
    </div>
  );
};

export default NovaPAPage;
